import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import Dropdown, { DropdownProps } from "./Dropdown";
import PreviewDropdown, { PreviewDropdownProps } from "./PreviewDropdown";
import { FiEyeOff } from "react-icons/fi";
import { useLingui } from "@lingui/react";
import { SoftRequiredIndicator } from "./SoftRequiredIndicator";

interface InputFormControlProps {
  id: string;
  label: string;
  inputType?: React.HTMLInputTypeAttribute;
  isRequired?: boolean;
  isSoftRequired?: boolean;
  value?: string | number;
  readOnly?: boolean;
  name?: string;
  onChangeValue?: Function;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  min?: string | number;
  max?: string;
  pattern?: string;
  error?: string | boolean;
  testId?: string;
}

export const InputFormControl: React.FC<InputFormControlProps> = ({
  id,
  label,
  inputType = "text",
  isRequired = false,
  isSoftRequired = false,
  value,
  readOnly = false,
  name = "",
  onChangeValue = () => {},
  onChange = () => {},
  onBlur = () => {},
  min,
  max,
  error,
  testId,
}) => {
  const onChangeCallback = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChangeValue(event.target.value);
      onChange(event);
    },
    [onChange, onChangeValue],
  );
  return (
    <FormControl
      id={id}
      isRequired={isRequired && !isSoftRequired}
      isInvalid={!!error}
    >
      <FormLabel>
        {label}
        {isSoftRequired && <SoftRequiredIndicator />}
      </FormLabel>
      <Input
        type={inputType}
        value={value}
        readOnly={readOnly}
        name={name}
        onBlur={onBlur}
        onChange={onChangeCallback}
        min={min}
        max={max}
        data-testid={testId}
      />
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

interface DropdownFormControlProps extends DropdownProps {
  id: string;
  label: string;
  isRequired?: boolean;
}

export const DropdownFormControl: React.FC<DropdownFormControlProps> = ({
  id,
  label,
  isRequired = false,
  ...props
}) => (
  <FormControl id={id} isRequired={isRequired}>
    <FormLabel>{label}</FormLabel>
    <Dropdown {...props} />
  </FormControl>
);

interface PreviewDropdownFormControlProps extends PreviewDropdownProps {
  id: string;
  label: string;
  isRequired?: boolean;
  isSoftRequired?: boolean;
  isInactive?: boolean;
}

export const PreviewDropdownFormControl: React.FC<
  PreviewDropdownFormControlProps
> = ({
  id,
  label,
  isRequired = false,
  isSoftRequired = false,
  isInactive = false,
  ...props
}) => {
  const { i18n } = useLingui();
  return (
    <FormControl id={id} isRequired={isRequired && !isSoftRequired}>
      {isInactive ? (
        <Flex align="center" justify="space-between" mb={2}>
          <FormLabel mb={0}>
            {label}
            {isSoftRequired && <SoftRequiredIndicator />}
          </FormLabel>
          <Tooltip label={i18n._("Option has been disabled")} hasArrow>
            <span data-testid="option-toggle-indicator-disabled">
              <FiEyeOff color="var(--chakra-colors-red-500)" />
            </span>
          </Tooltip>
        </Flex>
      ) : (
        <FormLabel>
          {label}
          {isSoftRequired && <SoftRequiredIndicator />}
        </FormLabel>
      )}
      <PreviewDropdown {...props} />
    </FormControl>
  );
};
